import React, {
  useRef,
  useState,
  useCallback,
  forwardRef,
  useEffect,
} from "react";
import { Editor } from "@tinymce/tinymce-react";
import InsertMediaModal from "../MediaManager/InsertMediaModal";
import { CDN_URL } from "../../services/config";

/**
 * TinyMCE React component wrapper for the TinyMCE rich text editor.
 * Provides media insertion via a custom modal and preserves caret position using bookmarks.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} props.value - Initial editor content.
 * @param {function(string): void} props.onChange - Callback fired when editor content changes.
 * @param {number|string} props.height - Height of the editor.
 * @param {boolean} [props.toolbar2=true] - Whether to show the second toolbar row.
 * @param {boolean} [props.statusBar=true] - Whether to show the status bar.
 * @param {boolean} [props.showCodeView=false] - Whether to show the code view button in the toolbar.
 * @param {Array<Object>} [props.mergeTags=[]] - Optional array of merge tags for editor integration.
 * Each merge tag can be a flat item or a parent with children for nested menus.
 * @example
 * [
 *   { label: "Date", value: "{{Date}}" },
 *   {
 *     label: "Person", // Parent tag
 *     children: [
 *       { label: "First Name", value: "{{Person.FirstName}}" },
 *       { label: "Last Name", value: "{{Person.LastName}}" }
 *     ]
 *   }
 * ]
 * @returns {JSX.Element} TinyMCE editor with media manager integration.
 */
const TinyMCE = forwardRef(
  (
    {
      value,
      onChange,
      height,
      toolbar2 = true,
      statusBar = true,
      showCodeView = false,
      mergeTags = [],
    },
    ref
  ) => {
    const editorRef = useRef(null);
    const bookmarkRef = useRef(null);
    const [open, setOpen] = useState(false);

    // 1. Store latest mergeTags in a ref
    const mergeTagsRef = useRef(mergeTags);
    useEffect(() => {
      mergeTagsRef.current = mergeTags;
    }, [mergeTags]);

    const handleMediaInsert = useCallback(
      (sourceType, mediaType, selectedMedia) => {
        let src;
        if (sourceType === "URL") {
          src = selectedMedia;
        } else {
          src = `${CDN_URL}${selectedMedia.path}`;
        }

        const editor = editorRef.current;
        if (!editor) return;

        // restore focus and caret position
        editor.focus();
        if (bookmarkRef.current) {
          editor.selection.moveToBookmark(bookmarkRef.current);
        }

        if (mediaType === "Image") {
          editor.insertContent(`<img src=\"${src}\" alt=\"\" />`);
        } else if (mediaType === "Video") {
          editor.insertContent(
            `<video controls><source src=\"${src}\" type=\"${selectedMedia.type}\" /></video>`
          );
        }

        // clear bookmark to avoid stale reference
        bookmarkRef.current = null;
        setOpen(false);
      },
      []
    );

    return (
      <>
        <InsertMediaModal
          open={open}
          setOpen={setOpen}
          onInsert={handleMediaInsert}
        />
        <Editor
          initialValue={value}
          onEditorChange={(content) => onChange(content)}
          tinymceScriptSrc="/tinymce/tinymce.min.js"
          licenseKey="gpl"
          init={{
            height,
            statusbar: statusBar,
            menubar: false,
            plugins: [
              "link lists advlist autolink autosave charmap code emoticons fullscreen image",
              "insertdatetime nonbreaking pagebreak preview save searchreplace table visualblocks wordcount",
            ].join(" "),
            toolbar1:
              "undo redo | bold italic underline strikethrough | fontfamily fontsize | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent",
            toolbar2: toolbar2
              ? `link | forecolor backcolor | removeformat | mediaManagerButton ${
                  mergeTags.length > 0 ? "| customDropdown" : ""
                }${showCodeView ? " | code" : ""}`
              : showCodeView
              ? "code"
              : "",
            setup: (editor) => {
              editorRef.current = editor;
              ref && (ref.current = editor);

              editor.on("SelectionChange", (e) => {
                // only track clicks within the editable area
                if (editor.getBody().contains(e.target)) {
                  bookmarkRef.current = editor.selection.getBookmark(2, true);
                }
              });

              // toolbar button launches modal; preserve bookmark
              editor.ui.registry.addButton("mediaManagerButton", {
                icon: "image",
                tooltip: "Media Manager",
                onAction: () => {
                  bookmarkRef.current = editor.selection.getBookmark(2, true);
                  setOpen(true);
                },
              });

              // 2. Use the ref in the fetch callback
              editor.ui.registry.addMenuButton("customDropdown", {
                text: "Insert Merge Tag",
                fetch: (callback) => {
                  let items = [];
                  (mergeTagsRef.current || []).forEach((tag) => {
                    if (tag.children && Array.isArray(tag.children)) {
                      // Parent with submenu
                      items.push({
                        type: "nestedmenuitem",
                        text: tag.label,
                        getSubmenuItems: () =>
                          tag.children.map((child) => ({
                            type: "menuitem",
                            text: child.label,
                            onAction: () => editor.insertContent(child.value),
                          })),
                      });
                    } else {
                      // Single item
                      items.push({
                        type: "menuitem",
                        text: tag.label,
                        onAction: () => editor.insertContent(tag.value),
                      });
                    }
                  });
                  callback(items);
                },
              });
            },
            font_formats:
              "Arial=arial,helvetica,sans-serif; Courier New=courier new,courier,monospace; Georgia=georgia,palatino; Tahoma=tahoma,arial,helvetica,sans-serif; Times New Roman=times new roman,times; Verdana=verdana,geneva;",
            fontsize_formats: "8pt 10pt 12pt 14pt 18pt 24pt 36pt",
          }}
        />
      </>
    );
  }
);

export default TinyMCE;
